import type { ReactNode } from "react";
import { Medal, Trophy, Crown, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

export type SubscriptionTier = "SILVER" | "GOLD" | "PLATINUM";

/**
 * Styling for subscription tiers.
 * Uses semantic Tailwind tokens: bg-* /10 for background, text-* for icon+text,
 * border-* /30 for border. Three distinct tiers with escalating prestige.
 *
 * WCAG 1.4.1 compliance: Each tier includes both icon AND text label, never
 * color alone as the distinguishing factor.
 */
const TIER_STYLES: Record<SubscriptionTier, { bg: string; text: string; label: string }> = {
  SILVER: {
    bg: "bg-muted/10 border-border/30",
    text: "text-muted-foreground",
    label: "Silver Subscription",
  },
  GOLD: {
    bg: "bg-warning/10 border-warning/30",
    text: "text-warning",
    label: "Gold Subscription",
  },
  PLATINUM: {
    bg: "bg-success/10 border-success/30",
    text: "text-success",
    label: "Platinum Subscription",
  },
};

/**
 * Icon component for each tier. Escalates in prestige: Medal → Trophy → Crown.
 */
const TIER_ICONS: Record<SubscriptionTier, ReactNode> = {
  SILVER: <Medal className="w-3.5 h-3.5" />,
  GOLD: <Trophy className="w-3.5 h-3.5" />,
  PLATINUM: <Crown className="w-3.5 h-3.5" />,
};

export interface SubscriptionTierBadgeProps {
  /** The subscription tier. When null, renders nothing (no badge for unpaid teachers). */
  tier: SubscriptionTier | null;
  /**
   * The tier is held on a free trial rather than paid for. Renders a distinct Sparkles icon and
   * the words "— Free trial" IN THE TEXT: a trial is not a purchase, and a border- or colour-only
   * treatment would be invisible to a screen reader and to anyone who cannot distinguish the two
   * palettes (WCAG 1.4.1).
   */
  trial?: boolean;
  /** Optional additional CSS classes for the badge container. */
  className?: string;
}

/**
 * SubscriptionTierBadge displays a teacher's active subscription tier.
 *
 * - **SILVER, GOLD, PLATINUM**: Shows tier icon + text + color (rounded pill badge).
 * - **null**: Returns nothing (no badge shown). A teacher without a subscription
 *   tier gets no badge — the absence is the signal.
 *
 * Styling follows the pattern from `TierHero.tsx` — semantic Tailwind color tokens,
 * rounded-full, `px-2.5 py-0.5`, text-xs font-semibold. Includes lucide-react icons
 * and aria-label for full WCAG 1.4.1 compliance (icon + text, never color alone).
 */
/** "PLATINUM" -> "Platinum" — the trial badge reads as a product name, not as an enum. */
function tierTitle(tier: SubscriptionTier): string {
  return tier.charAt(0) + tier.slice(1).toLowerCase();
}

export function SubscriptionTierBadge({ tier, trial = false, className }: SubscriptionTierBadgeProps) {
  // Return null when no subscription tier — no badge, no DOM node.
  if (!tier) {
    return null;
  }

  const style = TIER_STYLES[tier];
  const icon = trial ? <Sparkles className="w-3.5 h-3.5" /> : TIER_ICONS[tier];

  return (
    <span
      aria-label={trial ? `${tierTitle(tier)} free trial` : style.label}
      className={cn(
        "inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border",
        trial ? "bg-violet-100 border-violet-300 text-violet-900" : style.bg,
        trial ? "" : style.text,
        className
      )}
    >
      {icon}
      <span>{trial ? `${tierTitle(tier)} — Free trial` : tier}</span>
    </span>
  );
}

export default SubscriptionTierBadge;
